import { useUploadTellescopeFile } from "@tellescope/tanstack-query";
import { Button, Checkbox, Input, Label } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { ChangeEvent, useState } from "react";
import { toast } from "sonner";

export interface UploadFileFormProps {
  contactId: string;
  onUpload: () => void;
}

export const UploadFileForm = ({ contactId, onUpload }: UploadFileFormProps) => {
  const { t } = useTranslation("contacts");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dontShare, setDontShare] = useState(false);
  const [flagForReview, setFlagForReview] = useState(false);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);
  };

  const uploadFileMutation = useUploadTellescopeFile({
    prepareMutationOptions: {
      onError: () => {
        toast.error(t("There was an error uploading the file."));
      },
    },
    confirmMutationOptions: {
      onSuccess: () => {
        onUpload();
      },
      onError: () => {
        toast.error(t("There was an error uploading the file."));
      },
    },
  });

  const handleSubmit = async () => {
    if (!selectedFile) return;
    // The S3 upload stage rejects out of `uploadFileAsync` itself, between the prepare and confirm
    // mutations, so neither `onError` above sees it. This is the one failure the mutation callbacks
    // cannot reach, so it is caught here and reported with the same message.
    try {
      await uploadFileMutation.uploadFileAsync(
        {
          name: selectedFile.name,
          size: selectedFile.size,
          type: selectedFile.type,
          publicRead: false,
          isCalledOut: flagForReview,
          enduserId: contactId,
          hiddenFromEnduser: dontShare,
        },
        selectedFile
      );
    } catch {
      toast.error(t("There was an error uploading the file."));
    }
  };

  return (
    <div className="flex-1 overflow-y-auto space-y-6 pr-2">
      <div className="space-y-2">
        <Label htmlFor="file-upload" className="text-sm font-medium">
          {t("Upload File")}
        </Label>
        <Input id="file-upload" type="file" onChange={handleFileChange} />
      </div>

      {selectedFile && (
        <p className="text-sm text-muted-foreground">
          {t("Selected: {{name}} ({{size}} MB)", {
            name: selectedFile.name,
            size: (selectedFile.size / 1024 / 1024).toFixed(2),
          })}
        </p>
      )}

      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Checkbox id="dont-share" checked={dontShare} onCheckedChange={(checked) => setDontShare(checked === true)} />
          <Label htmlFor="dont-share" className="text-sm">
            {t("Don't share with contact")}
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="flag-review"
            checked={flagForReview}
            onCheckedChange={(checked) => setFlagForReview(checked === true)}
          />
          <Label htmlFor="flag-review" className="text-sm">
            {t("Flag for review")}
          </Label>
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t">
        {/* Also gated on the upload being in flight, so a second click during the multi-second S3
            round trip cannot create a duplicate file for the contact. */}
        <Button
          onClick={handleSubmit}
          className="w-full md:w-auto"
          disabled={!selectedFile || uploadFileMutation.isLoading}
        >
          {uploadFileMutation.isLoading ? t("Uploading...") : t("Upload File")}
        </Button>
      </div>
    </div>
  );
};
